import { Component, Inject, ViewEncapsulation, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UserStorageService } from '../services/user-storage.service';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-payment-data-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslocoModule],
  encapsulation: ViewEncapsulation.None,
  template: `
    <div class="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-50"
         *transloco="let t"
         (click)="onCancel()">
      <div class="bg-white shadow-xl max-w-md w-full mx-4 overflow-hidden flex flex-col" 
           style="box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);"
           (click)="$event.stopPropagation()">
        <!-- Header -->
        <div class="px-6 py-4 border-b bg-gray-100">
          <p class="text-sm font-medium text-gray-700">{{ t('invoices.payment.title') }}</p>
        </div>

        <!-- Content -->
        <div class="flex-1 overflow-y-auto p-6">
          <form (ngSubmit)="onSubmit()">
            <div class="space-y-4">
              <div>
                <label for="email" class="block text-sm font-medium text-gray-700 mb-1">
                  {{ t('invoices.payment.email') }} *
                </label>
                <input
                  id="email"
                  type="email"
                  [(ngModel)]="email"
                  name="email"
                  required
                  class="w-full px-3 py-2 border border-gray-300 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="seu@email.com">
                <p *ngIf="errors['email']" class="mt-1 text-xs text-red-600">{{ errors['email'] }}</p>
              </div>

              <div>
                <label for="firstName" class="block text-sm font-medium text-gray-700 mb-1">
                  {{ t('invoices.payment.firstName') }} *
                </label>
                <input
                  id="firstName"
                  type="text"
                  [(ngModel)]="firstName"
                  name="firstName"
                  required
                  class="w-full px-3 py-2 border border-gray-300 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="João">
                <p *ngIf="errors['firstName']" class="mt-1 text-xs text-red-600">{{ errors['firstName'] }}</p>
              </div>

              <div>
                <label for="lastName" class="block text-sm font-medium text-gray-700 mb-1">
                  {{ t('invoices.payment.lastName') }} *
                </label>
                <input
                  id="lastName"
                  type="text"
                  [(ngModel)]="lastName"
                  name="lastName"
                  required
                  class="w-full px-3 py-2 border border-gray-300 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="Silva">
                <p *ngIf="errors['lastName']" class="mt-1 text-xs text-red-600">{{ errors['lastName'] }}</p>
              </div>
            </div>
          </form>
        </div>

        <!-- Footer -->
        <div class="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
          <button 
            type="button"
            (click)="onCancel()"
            class="px-4 py-2 bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors">
            {{ t('common.cancel') }}
          </button>
          <button
            type="button"
            (click)="onSubmit()"
            class="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 transition-colors">
            {{ t('common.confirm') }}
          </button>
        </div>
      </div>
    </div>
  `,
  styles: [`
    :host {
      display: block;
    }
    :host ::ng-deep p {
      font-size: 0.75rem !important;
      line-height: 1rem !important;
      font-weight: 500 !important;
      color: #9ca3af !important;
    }
  `]
})
export class PaymentDataModalComponent implements OnInit {
  email: string = '';
  firstName: string = '';
  lastName: string = '';
  errors: { [key: string]: string } = {};

  constructor(
    public dialogRef: MatDialogRef<PaymentDataModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { 
      email?: string;
      firstName?: string;
      lastName?: string;
    },
    private userStorageService: UserStorageService,
    private transloco: TranslocoService
  ) {}

  ngOnInit(): void {
    // Primeiro, tenta usar os dados passados via data
    if (this.data?.email || this.data?.firstName || this.data?.lastName) {
      this.email = this.data.email || '';
      this.firstName = this.data.firstName || '';
      this.lastName = this.data.lastName || '';
    } else {
      // Se não houver dados passados, tenta carregar do localStorage
      const userData = this.userStorageService.getUserData();
      if (userData) {
        this.email = userData.email || '';
        this.firstName = userData.firstName || '';
        this.lastName = userData.lastName || '';
      }
    }
  }

  onSubmit(): void {
    this.errors = {};

    if (!this.email || !this.email.trim()) {
      this.errors['email'] = this.transloco.translate('invoices.errors.emailRequired');
    } else if (!this.isValidEmail(this.email)) {
      this.errors['email'] = this.transloco.translate('invoices.errors.emailInvalid');
    }

    if (!this.firstName || !this.firstName.trim()) {
      this.errors['firstName'] = this.transloco.translate('invoices.errors.firstNameRequired');
    }

    if (!this.lastName || !this.lastName.trim()) {
      this.errors['lastName'] = this.transloco.translate('invoices.errors.lastNameRequired');
    }

    if (Object.keys(this.errors).length === 0) {
      this.dialogRef.close({
        email: this.email.trim(),
        firstName: this.firstName.trim(),
        lastName: this.lastName.trim()
      });
    }
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }

  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }
}

